/**
 * Bid pricing block (#221): "Carrier gets ₹ / Shipper pays ₹" with a margin line.
 *
 * Either price can be edited; the last edit wins. A typed shipper price is
 * Fixed (sent as `shipperAmount`, whole rupees); a margin % or "Reset to
 * default %" makes it derived again (sent as `shipperMarginPct`). R10: never
 * both. The shipper figure shown while typing is a preview only — the server
 * computes the stored amount and the bid row shows that value (P3).
 */

export type PricingField = 'carrier' | 'shipper' | 'pct';
export type PricingMode = 'create' | 'edit';

export interface PricingState {
  carrier: string;
  /** What the "Shipper pays" input shows: typed (fixed) or a derived preview. */
  shipper: string;
  pct: string;
  fixed: boolean;
  lastEdited: PricingField | null;
  /** The shipper price was typed in this form (edit: changes the override). */
  shipperTouched: boolean;
  /** The pct was edited or reset in this form (edit: clears the override). */
  pctTouched: boolean;
}

import type { ShipperPricing } from '../../api/endpoints/admin';

export type { ShipperPricing };

const toNum = (v: string): number | null => {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function derive(carrier: string, pct: string): string {
  const c = toNum(carrier);
  const p = toNum(pct);
  if (c == null || c <= 0 || p == null) return '';
  return String(Math.round(c * (1 + p / 100)));
}

export function initPricing(init: {
  carrier: number | null;
  shipper: number | null;
  pct: number | null;
  fixed: boolean;
}): PricingState {
  const carrier = init.carrier != null ? String(init.carrier) : '';
  const pct = init.pct != null ? String(init.pct) : '';
  return {
    carrier,
    shipper: init.shipper != null ? String(init.shipper) : derive(carrier, pct),
    pct,
    fixed: init.fixed,
    lastEdited: null,
    shipperTouched: false,
    pctTouched: false,
  };
}

export function editCarrier(s: PricingState, carrier: string): PricingState {
  return {
    ...s,
    carrier,
    shipper: s.fixed ? s.shipper : derive(carrier, s.pct),
    lastEdited: 'carrier',
  };
}

export function editShipper(s: PricingState, shipper: string): PricingState {
  return { ...s, shipper, fixed: true, lastEdited: 'shipper', shipperTouched: true, pctTouched: false };
}

export function editPct(s: PricingState, pct: string): PricingState {
  return {
    ...s,
    pct,
    shipper: derive(s.carrier, pct),
    fixed: false,
    lastEdited: 'pct',
    shipperTouched: false,
    pctTouched: true,
  };
}

export function resetToDefault(s: PricingState, defaultPct: number): PricingState {
  return editPct(s, String(defaultPct));
}

/** Preview of the margin while typing: rupees and "%" over the carrier price (2 dp). */
export function previewMargin(s: PricingState): { amount: number; pct: string } | null {
  const c = toNum(s.carrier);
  const sh = toNum(s.shipper);
  if (c == null || c <= 0 || sh == null) return null;
  const amount = Math.round(sh) - c;
  const pct = (amount / c) * 100;
  return { amount, pct: `${pct < 0 ? '−' : ''}${Math.abs(pct).toFixed(2)}%` };
}

/** Rupees lost when the shipper pays less than the carrier gets, else null. */
export function lossAmount(s: PricingState): number | null {
  const m = previewMargin(s);
  return m && m.amount < 0 ? -m.amount : null;
}

/** Validation for a typed shipper price: whole rupees, at least ₹1. */
export function shipperAmountError(s: PricingState): string | null {
  if (!s.fixed) return null;
  const n = toNum(s.shipper);
  if (n == null || n < 1) return 'Shipper price must be at least ₹1.';
  if (!Number.isInteger(n)) return 'Shipper price must be in whole rupees.';
  return null;
}

/**
 * Which shipper field to send. Create sends the typed amount or the pct.
 * Edit sends only what was changed in this form: a carrier-only change sends
 * neither, so the server keeps a Fixed override or re-derives from the stored pct.
 */
export function buildShipperPricing(mode: PricingMode, s: PricingState): ShipperPricing {
  const amount = toNum(s.shipper);
  const pct = toNum(s.pct);
  if (mode === 'edit') {
    if (s.shipperTouched && s.fixed && amount != null) return { shipperAmount: amount };
    if (s.pctTouched && !s.fixed && pct != null) return { shipperMarginPct: pct };
    return undefined;
  }
  if (s.fixed && amount != null) return { shipperAmount: amount };
  if (pct != null) return { shipperMarginPct: pct };
  return undefined;
}

// ─── Display of server values (bid rows, pricing card) ────────────────────────

/** "+25%" / "−6.25%" / "+0%"; trims a whole number. */
export function signedPct(pct: number): string {
  const abs = Math.abs(pct);
  const text = abs % 1 === 0 ? abs.toFixed(0) : String(abs);
  return `${pct < 0 ? '−' : '+'}${text}%`;
}

/**
 * The bracket after a bid's shipper price: "Fixed, +25%", "Fixed, Loss, −6.25%", "+5%".
 * Server values only (`effectiveMarginPct`, falling back to the stored pct on an older API);
 * null when there is nothing to say (no margin data, a derived 0%).
 */
export function bidShipperMeta(b: {
  shipperMarginPct: number | null;
  shipperAmountFixed?: boolean | null;
  effectiveMarginPct?: number | null;
  negativeMargin?: boolean | null;
}): string | null {
  const pct = b.effectiveMarginPct ?? b.shipperMarginPct;
  const fixed = b.shipperAmountFixed === true;
  const parts: string[] = [];
  if (fixed) parts.push('Fixed');
  if (b.negativeMargin) parts.push('Loss');
  if (pct != null && (fixed || b.negativeMargin || pct > 0)) parts.push(signedPct(pct));
  return parts.length ? parts.join(', ') : null;
}

/** "₹12,000 (25.00%)" / "−₹3,000 (−6.25%)" from the server's margin fields. */
export function serverMarginText(amount: number, pct: number | null | undefined): string {
  const rupees = `${amount < 0 ? '−' : ''}₹${Math.abs(amount).toLocaleString('en-IN')}`;
  if (pct == null) return rupees;
  return `${rupees} (${pct < 0 ? '−' : ''}${Math.abs(pct).toFixed(2)}%)`;
}
